<template>
    <v-container fluid>
        <!-- Breadcrumbs -->
        <v-breadcrumbs
            :items="[
                { text: 'Home', disable: false, href: '/#/' },
                { text: 'Plants', disable: false, href: '/#/plants' },
                { text: 'Settings', disabled: true }
            ]"
            divider="/"
        ></v-breadcrumbs>

        <!-- Plant Species Dialog -->
        <PlantSpeciesEditDialog
            :dialog.sync="plantSpeciesDialog"
            :object.sync="editedPlantSpecies"
            v-on:item-created="plantSpeciesAdded"
        ></PlantSpeciesEditDialog>

        <!-- Plant Color Dialog -->
        <ColorEditDialog
            :dialog.sync="colorDialog"
            :object.sync="editedColor"
            v-on:item-created="colorAdded"
        ></ColorEditDialog>

        <!-- Plant Sort Dialog -->
        <TagEditDialog
            :dialog.sync="tagDialog"
            :object.sync="editedTag"
            v-on:item-created="tagAdded"
        ></TagEditDialog>

        <!-- Plant Environment Dialog -->
        <EnvironmentEditDialog
            :dialog.sync="environmentDialog"
            :object.sync="editedEnvironment"
            v-on:item-created="environmentAdded"
        ></EnvironmentEditDialog>

        <!-- Pot Size Dialog -->
        <PotSizeEditDialog
            :dialog.sync="potSizeDialog"
            :object.sync="editedPotSize"
            v-on:item-created="potSizeAdded"
        ></PotSizeEditDialog>

        <!-- Delete Supplier Dialog -->
        <DeleteDialog
            :dialog.sync="deleteDialog"
            :item="deletedItem"
            v-on:confirmed="deletingConfirmed"
        >
            <template v-slot:title
                >Delete - {{ deletedItem ? deletedItem.name : "" }}</template
            >
            <template v-slot:content
                >Are you sure you want to delete this
                {{ deletedItem.type }}?</template
            >
        </DeleteDialog>

        <v-card class="mx-3 elevation-5">
            <v-tabs centered show-arrows>
                <v-tabs-slider color="primary"></v-tabs-slider>

                <!-- tab header -->
                <v-tab>
                    <v-icon left>local_florist</v-icon>Plant Species
                </v-tab>
                <v-tab> <v-icon left>straighten</v-icon>Pot Sizes </v-tab>
                <v-tab> <v-icon left>color_lens</v-icon>Colors </v-tab>
                <v-tab> <v-icon left>blur_on</v-icon>Environments </v-tab>
                <v-tab> <v-icon left>turned_in_not</v-icon>Tags </v-tab>

                <!-- tab body -->
                <!-- Plant Species -->
                <v-tab-item>
                    <v-card flat>
                        <v-card-title class="pb-0">
                            <v-spacer></v-spacer>
                            <v-col cols="12" sm="4">
                                <v-text-field
                                    dense
                                    v-model="searchPlantSpeciesName"
                                    append-outer-icon="search"
                                    label="Search"
                                    clearable
                                    hide-details
                                ></v-text-field>
                            </v-col>
                            <v-spacer></v-spacer>
                            <v-btn
                                v-if="hasPermission('add_plantspecies')"
                                color="primary"
                                outlined
                                rounded
                                small
                                class="mx-2"
                                @click="addPlantSpecies()"
                                >Add</v-btn
                            >
                        </v-card-title>

                        <v-container fluid>
                            <v-data-table
                                :headers="plantSpeciesHeaders"
                                :loading="
                                    $apollo.queries.tenantPlantSpecies.loading
                                "
                                :items="plantSpecies"
                                :itemsPerPage="itemsPerPage"
                                :footer-props="{
                                    'items-per-page-options': [
                                        10,
                                        25,
                                        50,
                                        75,
                                        100,
                                        -1
                                    ]
                                }"
                                sortBy="node.name"
                                :search="searchPlantSpeciesName"
                                loading-text="Loading... Please wait"
                            >
                                <template v-slot:item.action="{ item }">
                                    <v-btn
                                        v-if="
                                            hasPermission('change_plantspecies')
                                        "
                                        outlined
                                        fab
                                        x-small
                                        color="primary"
                                        class="mr-2"
                                        @click="editPlantSpecies(item.node)"
                                    >
                                        <v-icon>edit</v-icon>
                                    </v-btn>
                                    <v-btn
                                        v-if="
                                            hasPermission('delete_plantspecies')
                                        "
                                        outlined
                                        fab
                                        x-small
                                        :disabled="
                                            item.node.plantSet.totalCount > 0
                                        "
                                        color="error"
                                        @click="deletingPlantSpecies(item.node)"
                                    >
                                        <v-icon>delete</v-icon>
                                    </v-btn>
                                    <!-- <v-icon small @click="deletingPlant(item)">delete</v-icon> -->
                                </template>
                            </v-data-table>
                        </v-container>
                    </v-card>
                </v-tab-item>

                <!-- Pot Size -->
                <v-tab-item>
                    <v-card flat>
                        <v-card-title class="pb-0">
                            <v-spacer></v-spacer>
                            <v-col cols="12" sm="4">
                                <v-text-field
                                    dense
                                    v-model="searchPotSize"
                                    append-outer-icon="search"
                                    label="Search"
                                    clearable
                                    hide-details
                                ></v-text-field>
                            </v-col>
                            <v-spacer></v-spacer>
                            <v-btn
                                v-if="hasPermission('add_potsize')"
                                color="primary"
                                outlined
                                rounded
                                small
                                class="mx-2"
                                @click="addPotSize()"
                                >Add</v-btn
                            >
                        </v-card-title>

                        <v-container fluid>
                            <v-data-table
                                :headers="potSizesHeaders"
                                :loading="
                                    $apollo.queries.tenantPotSizes.loading
                                "
                                :items="potSizes"
                                :itemsPerPage="itemsPerPage"
                                :footer-props="{
                                    'items-per-page-options': [
                                        10,
                                        25,
                                        50,
                                        75,
                                        100,
                                        -1
                                    ]
                                }"
                                :search="searchPotSize"
                                sortBy="node.potSizeCm"
                                loading-text="Loading... Please wait"
                            >
                                <template v-slot:item.node.potSizeCm="{ item }">
                                    <span>{{ item.node.potSizeCm }} cm</span>
                                </template>
                                <template v-slot:item.action="{ item }">
                                    <v-btn
                                        v-if="hasPermission('change_potsize')"
                                        outlined
                                        fab
                                        x-small
                                        color="primary"
                                        class="mr-2"
                                        @click="editPotSize(item.node)"
                                    >
                                        <v-icon>edit</v-icon>
                                    </v-btn>
                                    <v-btn
                                        v-if="hasPermission('delete_potsize')"
                                        outlined
                                        fab
                                        x-small
                                        color="error"
                                        :disabled="
                                            item.node.plantPotSizeSet
                                                .totalCount > 0
                                        "
                                        @click="deletingPotSize(item.node)"
                                    >
                                        <v-icon>delete</v-icon>
                                    </v-btn>
                                    <!-- <v-icon small @click="deletingPlant(item)">delete</v-icon> -->
                                </template>
                            </v-data-table>
                        </v-container>
                    </v-card>
                </v-tab-item>

                <!-- Colors -->
                <v-tab-item>
                    <v-card flat>
                        <v-card-title class="pb-0">
                            <v-spacer></v-spacer>
                            <v-col cols="12" sm="6">
                                <v-text-field
                                    dense
                                    v-model="searchColor"
                                    append-outer-icon="search"
                                    label="Search"
                                    clearable
                                    hide-details
                                ></v-text-field>
                            </v-col>
                            <v-spacer></v-spacer>
                            <v-btn
                                v-if="hasPermission('add_color')"
                                color="primary"
                                outlined
                                rounded
                                small
                                class="mx-2"
                                @click="addColor()"
                                >Add</v-btn
                            >
                        </v-card-title>

                        <v-container fluid>
                            <v-data-table
                                :headers="colorsHeaders"
                                :loading="$apollo.queries.tenantColors.loading"
                                :items="colors"
                                :itemsPerPage="itemsPerPage"
                                :footer-props="{
                                    'items-per-page-options': [
                                        10,
                                        25,
                                        50,
                                        75,
                                        100,
                                        -1
                                    ]
                                }"
                                :search="searchColor"
                                sortBy="node.color"
                                loading-text="Loading... Please wait"
                            >
                                <template v-slot:item.action="{ item }">
                                    <v-btn
                                        v-if="hasPermission('change_color')"
                                        outlined
                                        fab
                                        x-small
                                        color="primary"
                                        class="mr-2"
                                        @click="editColor(item.node)"
                                    >
                                        <v-icon>edit</v-icon>
                                    </v-btn>
                                    <v-btn
                                        v-if="hasPermission('delete_color')"
                                        outlined
                                        fab
                                        x-small
                                        color="error"
                                        :disabled="
                                            item.node.plantColorSet.totalCount >
                                            0
                                        "
                                        @click="deletingColor(item.node)"
                                    >
                                        <v-icon>delete</v-icon>
                                    </v-btn>
                                    <!-- <v-icon small @click="deletingPlant(item)">delete</v-icon> -->
                                </template>
                            </v-data-table>
                        </v-container>
                    </v-card>
                </v-tab-item>

                <!-- Environments -->
                <v-tab-item>
                    <v-card flat>
                        <v-card-title class="pb-0">
                            <v-spacer></v-spacer>
                            <v-col cols="12" sm="6">
                                <v-text-field
                                    dense
                                    v-model="searchEnvironment"
                                    append-outer-icon="search"
                                    label="Search"
                                    clearable
                                    hide-details
                                ></v-text-field>
                            </v-col>
                            <v-spacer></v-spacer>
                            <v-btn
                                v-if="hasPermission('add_environment')"
                                color="primary"
                                outlined
                                rounded
                                small
                                class="mx-2"
                                @click="addEnvironment()"
                                >Add</v-btn
                            >
                        </v-card-title>

                        <v-container fluid>
                            <v-data-table
                                :headers="environmentsHeaders"
                                :loading="
                                    $apollo.queries.tenantEnvironments.loading
                                "
                                :items="environments"
                                :itemsPerPage="itemsPerPage"
                                :footer-props="{
                                    'items-per-page-options': [
                                        10,
                                        25,
                                        50,
                                        75,
                                        100,
                                        -1
                                    ]
                                }"
                                :search="searchEnvironment"
                                sortBy="node.environment"
                                loading-text="Loading... Please wait"
                            >
                                <template v-slot:item.action="{ item }">
                                    <v-btn
                                        v-if="
                                            hasPermission('change_environment')
                                        "
                                        outlined
                                        fab
                                        x-small
                                        color="primary"
                                        class="mr-2"
                                        @click="editEnvironment(item.node)"
                                    >
                                        <v-icon>edit</v-icon>
                                    </v-btn>
                                    <v-btn
                                        v-if="
                                            hasPermission('delete_environment')
                                        "
                                        outlined
                                        fab
                                        x-small
                                        color="error"
                                        :disabled="
                                            item.node.plantEnvironmentSet
                                                .totalCount > 0
                                        "
                                        @click="deletingEnvironment(item.node)"
                                    >
                                        <v-icon>delete</v-icon>
                                    </v-btn>
                                    <!-- <v-icon small @click="deletingPlant(item)">delete</v-icon> -->
                                </template>
                            </v-data-table>
                        </v-container>
                    </v-card>
                </v-tab-item>

                <!-- Tags -->
                <v-tab-item>
                    <v-card flat>
                        <v-card-title class="pb-0">
                            <v-spacer></v-spacer>
                            <v-col cols="12" sm="6">
                                <v-text-field
                                    dense
                                    v-model="searchTag"
                                    append-outer-icon="search"
                                    label="Search"
                                    clearable
                                    hide-details
                                ></v-text-field>
                            </v-col>
                            <v-spacer></v-spacer>
                            <v-btn
                                v-if="hasPermission('add_tag')"
                                color="primary"
                                outlined
                                rounded
                                small
                                class="mx-2"
                                @click="addTag()"
                                >Add</v-btn
                            >
                        </v-card-title>

                        <v-container fluid>
                            <v-data-table
                                :headers="tagsHeaders"
                                :loading="$apollo.queries.tenantTags.loading"
                                :items="tags"
                                :itemsPerPage="itemsPerPage"
                                :footer-props="{
                                    'items-per-page-options': [
                                        10,
                                        25,
                                        50,
                                        75,
                                        100,
                                        -1
                                    ]
                                }"
                                :search="searchTag"
                                sortBy="node.tag"
                                loading-text="Loading... Please wait"
                            >
                                <template v-slot:item.action="{ item }">
                                    <v-btn
                                        v-if="hasPermission('change_tag')"
                                        outlined
                                        fab
                                        x-small
                                        color="primary"
                                        class="mr-2"
                                        @click="editTag(item.node)"
                                    >
                                        <v-icon>edit</v-icon>
                                    </v-btn>
                                    <v-btn
                                        v-if="hasPermission('delete_tag')"
                                        outlined
                                        fab
                                        x-small
                                        color="error"
                                        :disabled="
                                            item.node.plantTagSet.totalCount > 0
                                        "
                                        @click="deletingTag(item.node)"
                                    >
                                        <v-icon>delete</v-icon>
                                    </v-btn>
                                    <!-- <v-icon small @click="deletingPlant(item)">delete</v-icon> -->
                                </template>
                            </v-data-table>
                        </v-container>
                    </v-card>
                </v-tab-item>
            </v-tabs>
        </v-card>
    </v-container>
</template>

<script>
import gql from "graphql-tag";
import helper from "@/utils/helper.js";
import PlantSpeciesEditDialog from "@/components/plants/PlantSpeciesEditDialog.vue";
import ColorEditDialog from "@/components/plants/ColorEditDialog.vue";
import TagEditDialog from "@/components/plants/TagEditDialog.vue";
import EnvironmentEditDialog from "@/components/plants/EnvironmentEditDialog.vue";
import PotSizeEditDialog from "@/components/plants/PotSizeEditDialog.vue";
import DeleteDialog from "@/components/base/DeleteDialog.vue";

export default {
    name: "plant-settings-page",
    components: {
        PlantSpeciesEditDialog,
        ColorEditDialog,
        TagEditDialog,
        EnvironmentEditDialog,
        PotSizeEditDialog,
        DeleteDialog
    },

    apollo: {
        tenantPlantSpecies: {
            query: gql`
                query tenantPlantSpecies {
                    tenantPlantSpecies {
                        edges {
                            node {
                                id
                                name
                                plantSet {
                                    totalCount
                                }
                            }
                        }
                    }
                }
            `,
            variables() {},
            // Additional options here
            fetchPolicy: "cache-and-network",
            update: (data) => data.tenantPlantSpecies,
            skip: false,
            pollInterval: 60000 // ms
        },

        tenantPotSizes: {
            query: gql`
                query tenantPotSizes {
                    tenantPotSizes {
                        edges {
                            node {
                                id
                                potSizeCm
                                plantPotSizeSet {
                                    totalCount
                                }
                            }
                        }
                    }
                }
            `,
            variables() {},
            // Additional options here
            fetchPolicy: "cache-and-network",
            update: (data) => data.tenantPotSizes,
            skip: false,
            pollInterval: 60000 // ms
        },

        tenantColors: {
            query: gql`
                query tenantColors {
                    tenantColors {
                        edges {
                            node {
                                id
                                color
                                plantColorSet {
                                    totalCount
                                }
                            }
                        }
                    }
                }
            `,
            variables() {},
            // Additional options here
            fetchPolicy: "cache-and-network",
            update: (data) => data.tenantColors,
            skip: false,
            pollInterval: 60000 // ms
        },

        tenantEnvironments: {
            query: gql`
                query tenantEnvironments {
                    tenantEnvironments {
                        edges {
                            node {
                                id
                                environment
                                plantEnvironmentSet {
                                    totalCount
                                }
                            }
                        }
                    }
                }
            `,
            variables() {},
            // Additional options here
            fetchPolicy: "cache-and-network",
            update: (data) => data.tenantEnvironments,
            skip: false,
            pollInterval: 60000 // ms
        },

        tenantTags: {
            query: gql`
                query tenantTags {
                    tenantTags {
                        edges {
                            node {
                                id
                                tag
                                plantTagSet {
                                    totalCount
                                }
                            }
                        }
                    }
                }
            `,
            variables() {},
            // Additional options here
            fetchPolicy: "cache-and-network",
            update: (data) => data.tenantTags,
            skip: false,
            pollInterval: 60000 // ms
        }
    },

    data: function () {
        return {
            // isLoadingInventory: false,

            plantSpeciesDialog: false,
            colorDialog: false,
            tagDialog: false,
            environmentDialog: false,
            potSizeDialog: false,
            deleteDialog: false,
            searchPlantSpeciesName: "",
            searchPotSize: "",
            searchColor: "",
            searchEnvironment: "",
            searchTag: "",

            itemsPerPage: 25,
            // dataTableOptions: {},
            // page: 1,

            tenantPlantSpecies: {},
            tenantPotSizes: {},
            tenantColors: {},
            tenantEnvironments: {},
            tenantTags: {},

            // deleted item
            deletedItem: {
                id: null,
                type: null,
                name: null
            },

            // edited item
            editedPlantSpecies: {},
            editedColor: {},
            editedPotSize: {},
            editedEnvironment: {},
            editedTag: {},

            // table headers
            plantSpeciesHeaders: [
                {
                    text: "Plant Species",
                    value: "node.name",
                    sortable: true
                },
                {
                    text: "#Plants",
                    value: "node.plantSet.totalCount",
                    sortable: true
                },
                {
                    text: "Action",
                    align: "center",
                    value: "action",
                    width: "120px",
                    sortable: false
                }
            ],

            potSizesHeaders: [
                {
                    text: "Pot Size",
                    value: "node.potSizeCm",
                    sortable: true
                },
                {
                    text: "#Plants",
                    value: "node.plantPotSizeSet.totalCount",
                    sortable: true
                },
                {
                    text: "Action",
                    align: "center",
                    value: "action",
                    width: "120px",
                    sortable: false
                }
            ],

            colorsHeaders: [
                {
                    text: "Color",
                    value: "node.color",
                    sortable: true
                },
                {
                    text: "#Plants",
                    value: "node.plantColorSet.totalCount",
                    sortable: true
                },
                {
                    text: "Action",
                    align: "center",
                    value: "action",
                    width: "120px",
                    sortable: false
                }
            ],

            environmentsHeaders: [
                {
                    text: "Environment",
                    value: "node.environment",
                    sortable: true
                },
                {
                    text: "#Plants",
                    value: "node.plantEnvironmentSet.totalCount",
                    sortable: true
                },
                {
                    text: "Action",
                    align: "center",
                    value: "action",
                    width: "120px",
                    sortable: false
                }
            ],

            tagsHeaders: [
                {
                    text: "Tag",
                    value: "node.tag",
                    sortable: true
                },
                {
                    text: "#Plants",
                    value: "node.plantTagSet.totalCount",
                    sortable: true
                },
                {
                    text: "Action",
                    align: "center",
                    value: "action",
                    width: "120px",
                    sortable: false
                }
            ]
        };
    },
    computed: {
        // inventory: function() {
        //     return this.$store.state.inventory.inventory;
        // }
        plantSpecies() {
            return this.tenantPlantSpecies ? this.tenantPlantSpecies.edges : [];
        },
        potSizes() {
            return this.tenantPotSizes ? this.tenantPotSizes.edges : [];
        },
        colors() {
            return this.tenantColors ? this.tenantColors.edges : [];
        },
        environments() {
            return this.tenantEnvironments ? this.tenantEnvironments.edges : [];
        },
        tags() {
            return this.tenantTags ? this.tenantTags.edges : [];
        }
    },
    watch: {},
    created() {
        this.hasPermission = helper.hasPermission;
    },
    methods: {
        // plant species
        addPlantSpecies() {
            this.editedPlantSpecies = null;
            this.plantSpeciesDialog = true;
        },
        plantSpeciesAdded() {
            this.$apollo.queries.tenantPlantSpecies.refresh();
        },
        editPlantSpecies(plantSpecies) {
            this.editedPlantSpecies = JSON.parse(JSON.stringify(plantSpecies));
            this.plantSpeciesDialog = true;
        },
        deletingPlantSpecies(item) {
            this.deletedItem.type = "plant species";
            this.deletedItem.id = item.id;
            this.deletedItem.name = item.name;
            this.deleteDialog = true;
        },

        // color
        addColor() {
            this.editedColor = null;
            this.colorDialog = true;
        },
        colorAdded() {
            this.$apollo.queries.tenantColors.refresh();
        },
        editColor(color) {
            this.editedColor = JSON.parse(JSON.stringify(color));
            this.colorDialog = true;
        },
        deletingColor(item) {
            this.deletedItem.type = "color";
            this.deletedItem.id = item.id;
            this.deletedItem.name = item.color;
            this.deleteDialog = true;
        },

        // tag
        addTag() {
            this.editedTag = null;
            this.tagDialog = true;
        },
        tagAdded() {
            this.$apollo.queries.tenantTags.refresh();
        },
        editTag(tag) {
            this.editedTag = JSON.parse(JSON.stringify(tag));
            this.tagDialog = true;
        },
        deletingTag(item) {
            this.deletedItem.type = "tag";
            this.deletedItem.id = item.id;
            this.deletedItem.name = item.tag;
            this.deleteDialog = true;
        },

        // environment
        addEnvironment() {
            this.editedEnvironment = null;
            this.environmentDialog = true;
        },
        environmentAdded() {
            this.$apollo.queries.tenantEnvironments.refresh();
        },
        editEnvironment(environment) {
            this.editedEnvironment = JSON.parse(JSON.stringify(environment));
            this.environmentDialog = true;
        },
        deletingEnvironment(item) {
            this.deletedItem.type = "environment";
            this.deletedItem.id = item.id;
            this.deletedItem.name = item.environment;
            this.deleteDialog = true;
        },

        // pot sizes
        addPotSize() {
            this.editedPotSize = null;
            this.potSizeDialog = true;
        },
        potSizeAdded() {
            this.$apollo.queries.tenantPotSizes.refresh();
        },
        editPotSize(potSize) {
            this.editedPotSize = JSON.parse(JSON.stringify(potSize));
            this.potSizeDialog = true;
        },
        deletingPotSize(item) {
            this.deletedItem.type = "pot size";
            this.deletedItem.id = item.id;
            this.deletedItem.name = item.potSizeCm + " cm";
            this.deleteDialog = true;
        },

        // delete confirmed
        deletingConfirmed() {
            switch (this.deletedItem.type) {
                case "plant species":
                    this.callDeletePlantSpecies();
                    break;
                case "color":
                    this.callDeleteColor();
                    break;
                case "tag":
                    this.callDeleteTag();
                    break;
                case "environment":
                    this.callDeleteEnvironment();
                    break;
                case "pot size":
                    this.callDeletePotSize();
                    break;
                default:
                    return;
            }
        },

        callDeletePlantSpecies() {
            if (this.deletedItem) {
                const payload = {
                    id: this.deletedItem.id
                };

                this.$store
                    .dispatch("plantSpecies/deletePlantSpecies", payload)
                    .then((response) => {
                        // refresh plants
                        this.$apollo.queries.tenantPlantSpecies.refresh();

                        // close delete dialog
                        this.deleteDialog = false;

                        // show snackbar
                        const payload = {
                            color: "success",
                            message: `${response.name} is deleted!`
                        };
                        this.$store.dispatch("snackbar/showMessage", payload);
                    })
                    .catch((error) => {
                        this.error = error.graphQLErrors[0].message;
                    });
            }
        },

        callDeleteColor() {
            if (this.deletedItem) {
                const payload = {
                    id: this.deletedItem.id
                };

                this.$store
                    .dispatch("colors/deleteColor", payload)
                    .then((response) => {
                        // refresh plants
                        this.$apollo.queries.tenantColors.refresh();

                        // close delete dialog
                        this.deleteDialog = false;

                        // show snackbar
                        const payload = {
                            color: "success",
                            message: `${response.color} is deleted!`
                        };
                        this.$store.dispatch("snackbar/showMessage", payload);
                    })
                    .catch((error) => {
                        this.error = error.graphQLErrors[0].message;
                    });
            }
        },

        callDeleteTag() {
            if (this.deletedItem) {
                const payload = {
                    id: this.deletedItem.id
                };

                this.$store
                    .dispatch("tags/deleteTag", payload)
                    .then((response) => {
                        // refresh plants
                        this.$apollo.queries.tenantTags.refresh();

                        // close delete dialog
                        this.deleteDialog = false;

                        // show snackbar
                        const payload = {
                            color: "success",
                            message: `${response.tag} is deleted!`
                        };
                        this.$store.dispatch("snackbar/showMessage", payload);
                    })
                    .catch((error) => {
                        this.error = error.graphQLErrors[0].message;
                    });
            }
        },

        callDeleteEnvironment() {
            if (this.deletedItem) {
                const payload = {
                    id: this.deletedItem.id
                };

                this.$store
                    .dispatch("environments/deleteEnvironment", payload)
                    .then((response) => {
                        // refresh plants
                        this.$apollo.queries.tenantEnvironments.refresh();

                        // close delete dialog
                        this.deleteDialog = false;

                        // show snackbar
                        const payload = {
                            color: "success",
                            message: `${response.environment} is deleted!`
                        };
                        this.$store.dispatch("snackbar/showMessage", payload);
                    })
                    .catch((error) => {
                        this.error = error.graphQLErrors[0].message;
                    });
            }
        },

        callDeletePotSize() {
            if (this.deletedItem) {
                const payload = {
                    id: this.deletedItem.id
                };

                this.$store
                    .dispatch("potSizes/deletePotSize", payload)
                    .then((response) => {
                        // refresh plants
                        this.$apollo.queries.tenantPotSizes.refresh();

                        // close delete dialog
                        this.deleteDialog = false;

                        // show snackbar
                        const payload = {
                            color: "success",
                            message: `${response.potSizeCm} is deleted!`
                        };
                        this.$store.dispatch("snackbar/showMessage", payload);
                    })
                    .catch((error) => {
                        this.error = error.graphQLErrors[0].message;
                    });
            }
        }
    }
};
</script>